import { useState, useEffect, useCallback } from 'react';
import api from '../api/offlineClient';
import {
  MESES, fmtHoras, calcHorasVivas, mapsUrl, JORNADA_STD,
  TimeBox, FichajeMonthGrid, FichajeTable, Overlay, Fld,
  INP, BTN_NAV, BTN_SEC, LOC_LINK,
} from './Calendario';

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function fmtTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Acceso directo de admin/jefe/administrativo a lo relacionado con fichajes
// (fichar entrada/salida propia, calendario mensual con marcas, auditoría).
// Calendario.js sigue teniendo lo mismo integrado — esta página no le quita
// nada, es solo un atajo más centrado para no tener que bajar por toda la
// pantalla de Calendario.
export default function Fichajes() {
  const today    = new Date();
  const todayStr = isoDate(today);

  const [año, setAño] = useState(today.getFullYear());
  const [mes, setMes] = useState(today.getMonth());

  const [hoy,         setHoy]         = useState(null);
  const [fichandoIn,  setFichandoIn]  = useState(false);
  const [fichandoOut, setFichandoOut] = useState(false);

  const [festivos,         setFestivos]         = useState([]);
  const [allFichajes,      setAllFichajes]      = useState([]);
  const [allFichajesMonth, setAllFichajesMonth] = useState([]);
  const [filtroFecha,      setFiltroFecha]      = useState(todayStr);
  const [fichVista,        setFichVista]        = useState('mes');
  const [loading,          setLoading]          = useState(true);

  const [editFich,    setEditFich]    = useState(null);
  const [editEntrada, setEditEntrada] = useState('');
  const [editSalida,  setEditSalida]  = useState('');
  const [editSaving,  setEditSaving]  = useState(false);

  const loadHoy = useCallback(async () => {
    const res = await api.get(`/api/fichajes/hoy?fecha=${todayStr}`).catch(() => ({ data: null }));
    setHoy(res?.data || null);
  }, [todayStr]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const mesFmt = `${año}-${String(mes + 1).padStart(2, '0')}`;
      const [fest, fichD, fichM] = await Promise.all([
        api.get(`/api/calendar/festivos?año=${año}`).catch(() => ({ data: [] })),
        api.get(`/api/fichajes/all?fecha=${filtroFecha}`).catch(() => ({ data: [] })),
        api.get(`/api/fichajes/all/month?mes=${mesFmt}`).catch(() => ({ data: [] })),
      ]);
      setFestivos(fest.data || []);
      setAllFichajes(fichD.data || []);
      setAllFichajesMonth(fichM.data || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [año, mes, filtroFecha]);

  useEffect(() => { loadHoy(); }, [loadHoy]);
  useEffect(() => { load(); }, [load]);

  async function getPos() {
    return new Promise(resolve => {
      if (!navigator.geolocation) { resolve({}); return; }
      navigator.geolocation.getCurrentPosition(
        p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve({}), { timeout: 8000 }
      );
    });
  }

  async function fichar(tipo) {
    if (tipo === 'entrada') setFichandoIn(true); else setFichandoOut(true);
    try {
      const pos = await getPos();
      await api.post(`/api/fichajes/${tipo}`, { ...pos, fecha: todayStr });
      await loadHoy();
    } catch {
      alert('Error al registrar el fichaje. Inténtalo de nuevo.');
    } finally {
      setFichandoIn(false); setFichandoOut(false);
    }
  }

  function prevMes() { if (mes === 0) { setMes(11); setAño(a => a - 1); } else setMes(m => m - 1); }
  function nextMes() { if (mes === 11) { setMes(0); setAño(a => a + 1); } else setMes(m => m + 1); }

  function openEditFich(f) {
    const toLocalTime = ts => {
      if (!ts) return '';
      const d = new Date(ts);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    setEditFich(f);
    setEditEntrada(toLocalTime(f.entrada));
    setEditSalida(toLocalTime(f.salida));
  }

  async function saveEditFich() {
    setEditSaving(true);
    try {
      const fecha = editFich.fecha.slice(0, 10);
      const entradaISO = editEntrada ? new Date(`${fecha}T${editEntrada}:00`).toISOString() : null;
      const salidaISO  = editSalida  ? new Date(`${fecha}T${editSalida}:00`).toISOString()  : null;
      await api.put(`/api/fichajes/${editFich.id}`, { entrada: entradaISO, salida: salidaISO });
      setEditFich(null);
      await load();
    } catch (e) { alert('Error al guardar: ' + (e.response?.data?.error || e.message)); }
    finally { setEditSaving(false); }
  }

  const horasVivas = hoy ? calcHorasVivas(hoy.entrada, hoy.salida) : null;
  const extras = horasVivas != null && horasVivas > JORNADA_STD ? horasVivas - JORNADA_STD : null;
  const jornadaAbierta = hoy?.entrada && !hoy?.salida;

  if (loading) return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>;

  return (
    <div className="sg-page" style={{ padding: 'clamp(14px,4vw,28px)', maxWidth: 1000, margin: '0 auto', width: '100%' }}>

      <div style={{ marginBottom: 20, animation: 'sg-fade-up .4s var(--ease) both' }}>
        <h1 style={{ fontSize: 24, fontWeight: 900, letterSpacing: -.4, marginBottom: 4 }}>⏱️ Fichajes</h1>
        <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>Tu fichaje del día y el calendario del equipo</p>
      </div>

      {/* ── Fichaje propio ─────────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--surface)',
        border: `1.5px solid ${jornadaAbierta ? 'color-mix(in srgb, var(--ok) 40%, transparent)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-lg)', padding: '18px 20px', marginBottom: 20,
        boxShadow: jornadaAbierta ? '0 0 24px color-mix(in srgb, var(--ok) 8%, transparent)' : 'none',
        animation: 'sg-fade-up .4s var(--ease) .04s both',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className={`sg-tile${jornadaAbierta ? ' sg-pulse' : ''}`} style={{
                '--tile': jornadaAbierta ? 'var(--ok)' : 'var(--text-dim)',
                width: 44, height: 44, borderRadius: 'var(--radius-md)', fontSize: 20, flexShrink: 0,
              }}>⏱️</div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>Tu fichaje</div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 1 }}>
                  {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' })}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 28 }}>
              <TimeBox label="Entrada" val={fmtTime(hoy?.entrada)} color={hoy?.entrada ? 'var(--ok)' : 'var(--text-dim)'} />
              <TimeBox label="Salida"  val={fmtTime(hoy?.salida)}  color={hoy?.salida  ? 'var(--bad)' : 'var(--text-dim)'} />
              {horasVivas != null && <TimeBox label="Horas" val={fmtHoras(horasVivas)} color={extras != null ? 'var(--bad)' : 'var(--amber)'} extras={extras} />}
            </div>
          </div>
          <div>
            {!hoy?.entrada && (
              <button onClick={() => fichar('entrada')} disabled={fichandoIn} style={{
                background: 'color-mix(in srgb, var(--ok) 14%, transparent)',
                border: '1.5px solid color-mix(in srgb, var(--ok) 45%, transparent)', color: 'var(--ok)',
                padding: '11px 22px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 800, fontSize: 14,
              }}>
                {fichandoIn ? '📍 Localizando…' : '▶ Fichar Entrada'}
              </button>
            )}
            {hoy?.entrada && !hoy?.salida && (
              <button onClick={() => fichar('salida')} disabled={fichandoOut} style={{
                background: 'color-mix(in srgb, var(--bad) 14%, transparent)',
                border: '1.5px solid color-mix(in srgb, var(--bad) 45%, transparent)', color: 'var(--bad)',
                padding: '11px 22px', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontWeight: 800, fontSize: 14,
              }}>
                {fichandoOut ? '📍 Localizando…' : '■ Fichar Salida'}
              </button>
            )}
            {hoy?.entrada && hoy?.salida && (
              <span style={{ fontSize: 13, color: 'var(--ok)', fontWeight: 800 }}>✓ Jornada completada</span>
            )}
          </div>
        </div>
        {(hoy?.lat_entrada || hoy?.lat_salida) && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 10, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {hoy.lat_entrada && <a href={mapsUrl(hoy.lat_entrada, hoy.lng_entrada)} target="_blank" rel="noreferrer" style={LOC_LINK}>📍 Entrada: {parseFloat(hoy.lat_entrada).toFixed(5)}, {parseFloat(hoy.lng_entrada).toFixed(5)}</a>}
            {hoy.lat_salida  && <a href={mapsUrl(hoy.lat_salida,  hoy.lng_salida)}  target="_blank" rel="noreferrer" style={LOC_LINK}>📍 Salida: {parseFloat(hoy.lat_salida).toFixed(5)}, {parseFloat(hoy.lng_salida).toFixed(5)}</a>}
          </div>
        )}
      </div>

      {/* ── Calendario mensual con marcas + auditoría ─────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', marginBottom: 14, animation: 'sg-fade-up .4s var(--ease) .08s both' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="sg-tile--soft" style={{ '--tile': '#06b6d4', width: 34, height: 34, borderRadius: 9, fontSize: 15, flexShrink: 0 }}>📅</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>Calendario del equipo</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', borderRadius: 7, overflow: 'hidden', border: '1px solid var(--border)' }}>
              <button onClick={() => setFichVista('mes')}
                style={{ padding: '5px 13px', fontSize: 11, fontWeight: 700, cursor: 'pointer', border: 'none', background: fichVista === 'mes' ? 'var(--amber)' : 'var(--surface2)', color: fichVista === 'mes' ? '#000' : 'var(--text-dim)' }}>
                Mes
              </button>
              <button onClick={() => setFichVista('dia')}
                style={{ padding: '5px 13px', fontSize: 11, fontWeight: 700, cursor: 'pointer', border: 'none', background: fichVista === 'dia' ? 'var(--amber)' : 'var(--surface2)', color: fichVista === 'dia' ? '#000' : 'var(--text-dim)' }}>
                Día
              </button>
            </div>
            {fichVista === 'mes' ? (
              <>
                <button onClick={prevMes} style={BTN_NAV}>‹</button>
                <div style={{ fontWeight: 800, fontSize: 13, color: 'var(--text)', minWidth: 110, textAlign: 'center' }}>{MESES[mes]} {año}</div>
                <button onClick={nextMes} style={BTN_NAV}>›</button>
              </>
            ) : (
              <input type="date" value={filtroFecha} onChange={e => setFiltroFecha(e.target.value)} style={{ ...INP, width: 'auto' }} />
            )}
          </div>
        </div>
        {fichVista === 'dia' ? (
          allFichajes.length === 0
            ? <div style={{ color: 'var(--text-dim)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>Sin fichajes para este día.</div>
            : <FichajeTable rows={allFichajes} onEdit={openEditFich} />
        ) : (
          allFichajesMonth.length === 0
            ? <div style={{ color: 'var(--text-dim)', fontSize: 12, textAlign: 'center', padding: '20px 0' }}>Sin fichajes para {MESES[mes]} {año}.</div>
            : <FichajeMonthGrid rows={allFichajesMonth} año={año} mes={mes} festivos={festivos} onEdit={openEditFich} />
        )}
      </div>

      {editFich && (
        <Overlay onClose={() => setEditFich(null)}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 4 }}>Editar Fichaje</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 14 }}>
            {editFich.user_nombre} &middot; {editFich.fecha?.slice(0, 10)}
          </div>
          <Fld label="Entrada">
            <input type="time" value={editEntrada} onChange={e => setEditEntrada(e.target.value)} style={INP} />
          </Fld>
          <Fld label="Salida">
            <input type="time" value={editSalida} onChange={e => setEditSalida(e.target.value)} style={INP} />
          </Fld>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={saveEditFich} disabled={editSaving || !editEntrada}
              style={{ flex: 1, background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 0', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13, opacity: !editEntrada ? .5 : 1 }}>
              {editSaving ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button onClick={() => setEditFich(null)} style={BTN_SEC}>Cancelar</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}
